import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { X, Plus, Image as ImageIcon, Upload } from 'lucide-react';
import { tags as availableTags } from '../../data/mock';

const ArticleEditor = ({ article, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: article?.title || '',
    excerpt: article?.excerpt || '',
    content: article?.content || '',
    image: article?.image || '',
    tags: article?.tags || [],
    author: article?.author || '',
    category: article?.category || 'chien'
  });

  const [newTag, setNewTag] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
    }
    setNewTag('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  // Sample images for quick selection
  const sampleImages = [
    'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=600',
    'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=600',
    'https://images.unsplash.com/photo-1585110396000-c9ffd4e4b308?w=600',
    'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=600',
    'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=600',
    'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=600',
    'https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?w=600',
    'https://images.unsplash.com/photo-1495360010541-f48722b34f7d?w=600',
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {article ? 'Modifier l\'article' : 'Nouvel article'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <Label htmlFor="title">Titre de l'article *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Ex: Comment bien nourrir son chat ?"
              className="mt-1"
              required
            />
          </div>

          {/* Excerpt */}
          <div>
            <Label htmlFor="excerpt">Résumé (extrait) *</Label>
            <Textarea
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              placeholder="Court résumé de l'article (affiché dans la liste)"
              className="mt-1"
              rows={3}
              required
            />
          </div>

          {/* Image */}
          <div>
            <Label>Image de couverture *</Label>
            <div className="mt-2 space-y-3">
              {/* Current image preview */}
              {formData.image && (
                <div className="relative w-full h-48 rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={formData.image}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleChange('image', '')}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Image URL input */}
              <div className="flex gap-2">
                <Input
                  value={formData.image}
                  onChange={(e) => handleChange('image', e.target.value)}
                  placeholder="URL de l'image (https://...)"
                  className="flex-1"
                />
              </div>

              {/* Quick image selection */}
              <div>
                <p className="text-sm text-gray-500 mb-2">Ou choisissez une image :</p>
                <div className="grid grid-cols-4 gap-2">
                  {sampleImages.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleChange('image', img)}
                      className={`relative h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        formData.image === img ? 'border-green-500' : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div>
            <Label htmlFor="content">Contenu de l'article *</Label>
            <p className="text-xs text-gray-500 mb-1">Vous pouvez utiliser le format Markdown (## pour les titres, - pour les listes, **gras**)</p>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => handleChange('content', e.target.value)}
              placeholder="Rédigez votre article ici..."
              className="mt-1 font-mono text-sm"
              rows={12}
              required
            />
          </div>

          {/* Tags */}
          <div>
            <Label>Tags</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-green-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {availableTags.filter(t => t !== 'Tous' && !formData.tags.includes(t)).map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleAddTag(tag)}
                  className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Author */}
          <div>
            <Label htmlFor="author">Auteur</Label>
            <Input
              id="author"
              value={formData.author}
              onChange={(e) => handleChange('author', e.target.value)}
              placeholder="Ex: Dr. Marie Lambert"
              className="mt-1"
            />
          </div>

          {/* Category */}
          <div>
            <Label htmlFor="category">Catégorie principale</Label>
            <select
              id="category"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 p-2"
            >
              <option value="chien">Chien</option>
              <option value="chat">Chat</option>
              <option value="nac">NAC</option>
              <option value="adoption">Adoption</option>
              <option value="sante">Santé</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" className="bg-green-500 hover:bg-green-600 text-white">
              {article ? 'Enregistrer les modifications' : 'Publier l\'article'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ArticleEditor;
